import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  EmailOutlined as EmailOutlinedIcon,
  History as HistoryIcon,
  LockOpenOutlined as LockOpenOutlinedIcon,
  PaymentsOutlined as PaymentsOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  ReceiptLong as ReceiptLongIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { auth, db, functions } from "../firebase";
import { useAuth } from "../context/AuthContext";

const CLOUD_FUNCTION_BASE =
  "https://us-central1-mylocalforce-295b8.cloudfunctions.net";
const AUTH_ERROR_PATTERN = /auth|unauth|permission|sign in/i;

const getAdminIdToken = async (forceRefresh = false, explicitUser = null) => {
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }

  const user = explicitUser || auth.currentUser;

  if (!user?.getIdToken) {
    return "";
  }

  return user.getIdToken(forceRefresh);
};

const callAdminFunction = async (functionName, payload = {}, explicitUser = null) => {
  const callable = httpsCallable(functions, functionName);
  const idToken = payload.idToken || (await getAdminIdToken(false, explicitUser));
  if (!idToken) {
    const error = new Error("Firebase sign-in token missing. Please sign out and sign in again.");
    error.code = "TOKEN_MISSING";
    throw error;
  }

  if (process.env.NODE_ENV !== "production") {
    console.info(`[${functionName}] calling with admin token`, {
      uid: explicitUser?.uid || auth.currentUser?.uid || null,
      email: explicitUser?.email || auth.currentUser?.email || null,
      tokenPresent: Boolean(idToken),
    });
  }

  const callablePayload = idToken ? { ...payload, idToken } : payload;

  try {
    const result = await callable(callablePayload);
    return result.data || {};
  } catch (error) {
    const retryUser = explicitUser || auth.currentUser;
    if (retryUser && AUTH_ERROR_PATTERN.test(String(error?.message || error?.code || ""))) {
      const refreshedToken = await getAdminIdToken(true, retryUser);
      if (!refreshedToken) {
        const tokenError = new Error("Firebase sign-in token missing. Please sign out and sign in again.");
        tokenError.code = "TOKEN_MISSING";
        throw tokenError;
      }

      if (process.env.NODE_ENV !== "production") {
        console.info(`[${functionName}] retrying with refreshed admin token`, {
          uid: retryUser.uid || null,
          email: retryUser.email || null,
          tokenPresent: Boolean(refreshedToken),
        });
      }

      const retryResult = await callable(
        refreshedToken ? { ...payload, idToken: refreshedToken } : payload,
      );
      return retryResult.data || {};
    }

    throw error;
  }
};
const PLATFORM_FEE_PER_ORDER = 5;
const GST_RATE = 0.1;
const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const money = (value) => {
  const parsed = asNumber(value, NaN);
  if (!Number.isFinite(parsed)) return "N/A";
  return `$${parsed.toFixed(2)}`;
};

const firstFiniteNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return NaN;
};
const pillControlSx = {
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 500,
  boxShadow: "none",
};
const summaryChipSx = {
  height: 36,
  borderRadius: 999,
  fontWeight: 500,
  borderWidth: 1,
  transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  },
};
const invoiceActionButtonSx = {
  ...pillControlSx,
  px: 1.5,
  minHeight: 36,
  borderRadius: 999,
  borderColor: "primary.main",
  color: "primary.main",
  backgroundColor: "background.paper",
  "&:hover": {
    boxShadow: "none",
    borderColor: "primary.dark",
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
  },
};
const paymentPanelSx = {
  p: 1.5,
  minWidth: 0,
  alignSelf: "stretch",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1.5,
  backgroundColor: "background.paper",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
};
const amountBadgeSx = {
  height: 26,
  borderRadius: 999,
  fontWeight: 500,
  border: "1px solid",
  borderColor: "primary.light",
  color: "primary.main",
  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
};

const statusBadgeSx = (tone = "primary") => ({
  height: 24,
  borderRadius: 999,
  fontWeight: 500,
  border: "1px solid",
  borderColor: `${tone}.light`,
  color: `${tone}.dark`,
  backgroundColor: (theme) => alpha(theme.palette[tone]?.main || theme.palette.primary.main, 0.1),
});

const datetimeLabel = (value) => {
  if (!value) return "N/A";
  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString();
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const dateLabel = (value) => {
  if (!value) return "N/A";
  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleDateString();
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString();
};

const buildPayoutReference = () => {
  const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `MLF-PAY-${datePart}-${randomPart}`;
};

const resolveOrderNumber = (booking) =>
  booking.orderNumber || booking.order_reference || booking.id;

const resolveClientPayment = (booking) => {
  const serviceAmount = firstFiniteNumber(
    booking.serviceTotal,
    booking.price,
    booking.basePrice
  );
  const explicitPlatformFee = firstFiniteNumber(
    booking.platformFee,
    booking.accounting?.platformFee
  );
  const servicePlusPlatform = Number.isFinite(serviceAmount)
    ? serviceAmount + (Number.isFinite(explicitPlatformFee) ? explicitPlatformFee : 0)
    : NaN;

  return Math.max(
    0,
    firstFiniteNumber(
      booking.amountBeforeStripe,
      booking.totalAmount,
      booking.estimatedTotalAmount,
      servicePlusPlatform,
      booking.totalAmountPaid,
      booking.price,
      0
    )
  );
};

const resolveServiceAmount = (booking, clientPayment, platformFee) =>
  Math.max(
    0,
    asNumber(
      booking.serviceTotal ??
        booking.price ??
        booking.basePrice ??
        Math.max(0, clientPayment - platformFee),
      Math.max(0, clientPayment - platformFee)
    )
  );

const resolvePlatformFee = (booking, clientPayment, serviceAmount, fallbackFee) => {
  const direct = asNumber(
    booking.platformFee ?? booking.accounting?.platformFee,
    NaN
  );
  if (Number.isFinite(direct)) return Math.max(0, direct);

  const inferred = clientPayment - serviceAmount;
  if (inferred > 0) return inferred;

  return Math.max(0, fallbackFee);
};

const gstIncluded = (amount) => {
  const gross = Math.max(0, asNumber(amount, 0));
  const gst = gross * (GST_RATE / (1 + GST_RATE));
  return {
    gst,
    net: gross - gst,
  };
};

const resolveGatewayChargeEstimate = (booking, clientPayment) => {
  const direct = asNumber(
    booking.stripeCharge ?? booking.gatewayCharge ?? booking.paymentGatewayCharge,
    NaN
  );
  if (Number.isFinite(direct)) return Math.max(0, direct);

  const cardType = String(booking.stripeCardType || "domestic").toLowerCase();
  const percentage = cardType === "international" ? 0.035 : 0.017;
  const fixed = clientPayment > 0 ? 0.3 : 0;
  return Math.max(0, clientPayment * percentage + fixed);
};

const resolveInvoiceNumber = (booking) =>
  booking.invoiceNumber || booking.invoice?.number || booking.orderInvoiceNumber || "";

const resolveInvoiceAmount = (booking, fallbackAmount) =>
  asNumber(
    booking.invoiceAmount ?? booking.invoice?.amount ?? booking.invoicedAmount,
    fallbackAmount
  );

const resolveInvoiceUrl = (booking) =>
  booking.invoiceUrl || booking.invoicePDF || booking.invoice?.pdfUrl || booking.invoice?.url || "";

const resolveInvoiceDelivery = (booking) => {
  const invoiceNumber = resolveInvoiceNumber(booking);
  const invoiceStatus = String(
    booking.invoiceStatus || booking.invoice?.status || ""
  ).toLowerCase();
  const invoiceSent =
    booking.invoiceSent === true ||
    booking.invoice?.sent === true ||
    ["sent", "emailed", "delivered"].includes(invoiceStatus) ||
    Boolean(booking.invoiceSentAt || booking.invoice?.sentAt);
  const invoiceSending = ["sending", "generating", "processing"].includes(invoiceStatus);
  const invoiceFailed =
    ["failed", "error"].includes(invoiceStatus) ||
    Boolean(booking.invoiceError || booking.invoice?.error);

  let label = "Invoice Pending";
  let color = "default";

  if (invoiceFailed) {
    label = "Invoice Failed";
    color = "error";
  } else if (invoiceSent) {
    label = "Invoice Sent";
    color = "success";
  } else if (invoiceSending) {
    label = "Invoice Sending";
    color = "info";
  } else if (invoiceNumber) {
    label = "Invoice Ready";
    color = "warning";
  }

  return {
    sent: invoiceSent,
    sending: invoiceSending,
    failed: invoiceFailed,
    label,
    color,
    sentAt: booking.invoiceSentAt || booking.invoice?.sentAt || null,
    emailTo: booking.invoiceEmailTo || booking.invoice?.emailTo || booking.customerEmail || "",
    url: resolveInvoiceUrl(booking),
  };
};

const resolveFinalCharges = (booking, gatewayEstimate) => {
  const finalCharges = asNumber(
    booking.bankStripeChargesFinal ??
      booking.bankReconciliation?.finalCharges ??
      booking.finalGatewayCharges,
    NaN
  );

  if (Number.isFinite(finalCharges)) return Math.max(0, finalCharges);
  return Math.max(0, gatewayEstimate);
};

const resolveNetCredited = (booking, clientPayment, finalCharges) => {
  const direct = asNumber(
    booking.netAmountCreditedToBank ?? booking.bankReconciliation?.actualDepositAmount,
    NaN
  );
  if (Number.isFinite(direct)) return Math.max(0, direct);

  return Math.max(0, clientPayment - finalCharges);
};

const resolveCommissionRate = (booking, providerCommissionRates, defaultRate) => {
  const providerId = booking.providerId || booking.clientId;
  if (providerId && providerCommissionRates[providerId] != null) {
    return asNumber(providerCommissionRates[providerId], defaultRate);
  }
  return asNumber(booking.commissionRate, defaultRate);
};

const resolveCommissionAmount = (booking, serviceBaseAmount, commissionRate) => {
  const direct = asNumber(
    booking.accounting?.commission ?? booking.commissionAmount,
    NaN
  );
  if (Number.isFinite(direct)) return Math.max(0, direct);

  return Math.max(0, (serviceBaseAmount * commissionRate) / 100);
};

const resolveProviderFee = (
  booking,
  clientPayment,
  commissionAmount,
  platformFee
) => {
  const direct = asNumber(
    booking.payoutAmount ??
      booking.payout?.amount ??
      booking.serviceProviderFee,
    NaN
  );
  if (Number.isFinite(direct)) return Math.max(0, direct);

  return Math.max(0, clientPayment - commissionAmount - platformFee);
};

const resolveProviderInvoiceDelivery = (booking) => {
  const status = String(
    booking.providerInvoiceStatus || booking.providerInvoice?.status || ""
  ).toLowerCase();
  const sent =
    booking.providerInvoiceSent === true ||
    booking.providerInvoice?.sent === true ||
    ["sent", "emailed", "delivered"].includes(status) ||
    Boolean(booking.providerInvoiceSentAt || booking.providerInvoice?.sentAt);
  const sending = ["sending", "generating", "processing"].includes(status);
  const held = ["held", "pending_customer_review"].includes(status);
  const awaitingPayout = ["awaiting_payout", "pending_payout"].includes(status);
  const failed =
    ["failed", "error"].includes(status) ||
    Boolean(booking.providerInvoiceError || booking.providerInvoice?.error);

  let label = "Provider Invoice Pending";
  let color = "default";

  if (failed) {
    label = "Provider Invoice Failed";
    color = "error";
  } else if (sent) {
    label = "Provider Invoice Sent";
    color = "success";
  } else if (sending) {
    label = "Provider Invoice Sending";
    color = "info";
  } else if (held) {
    label = status === "held" ? "Provider Invoice Held" : "Pending Review";
    color = "warning";
  } else if (awaitingPayout) {
    label = "Awaiting Payout";
    color = "warning";
  }

  return {
    sent,
    sending,
    held,
    failed,
    label,
    color,
    holdReason: booking.providerInvoiceHoldReason || booking.payoutHoldReason || "",
    emailTo:
      booking.providerInvoiceEmailTo ||
      booking.providerInvoice?.emailTo ||
      booking.providerEmail ||
      "",
  };
};

const resolveProviderDisplay = (booking, providerProfiles) => {
  const providerId = booking.providerId || booking.clientId || "";
  const providerProfile = providerId ? providerProfiles[providerId] || {} : {};
  const providerName =
    booking.providerName ||
    booking.clientName ||
    providerProfile.businessName ||
    providerProfile.displayName ||
    providerProfile.name ||
    "Unassigned";
  const providerEmail =
    booking.providerEmail ||
    booking.clientEmail ||
    providerProfile.email ||
    "";

  return {
    id: providerId,
    name: providerName,
    email: providerEmail,
  };
};

const resolveStripeVerificationStatus = (booking) => {
  const paymentStatus = String(booking.paymentStatus || "").toLowerCase();
  const verified = booking.paymentVerification?.verified === true;

  if (verified || paymentStatus === "paid") {
    return { label: "Verified", color: "success" };
  }
  if (paymentStatus === "paid_manual") {
    return { label: "Manual Confirmed", color: "warning" };
  }
  if (paymentStatus === "refunded" || paymentStatus === "partially_refunded") {
    return { label: "Refunded", color: "default" };
  }
  if (paymentStatus === "failed") {
    return { label: "Failed", color: "error" };
  }
  return { label: "Pending", color: "info" };
};

const isPaymentVerified = (row) =>
  row.verificationStatus.label === "Verified" ||
  row.verificationStatus.label === "Manual Confirmed";

const isPayoutConfirmed = (row) =>
  row.booking.payout?.paid === true ||
  row.booking.commissionPaid === true ||
  Boolean(row.booking.payoutReferenceNumber || row.booking.transferId);

const isProfitBooked = (row) =>
  row.booking.profitBooked === true ||
  row.tracker.steps.some((step) => step.key === "profit" && step.done);

const isReconciledBooking = (row) =>
  row.booking.collectionReconciled === true ||
  row.booking.bankReconciliation?.reconciled === true;

const hasStripeReference = (row) =>
  Boolean(row.paymentReference && row.paymentReference !== "N/A");

const isBookingComplete = (row) => {
  const booking = row.booking || {};
  const status = String(booking.status || booking.bookingStatus || "").toLowerCase();
  return (
    status === "completed" ||
    booking.serviceCompleted === true ||
    booking.completed === true ||
    Boolean(booking.completedAt)
  );
};

const hasRefundActivity = (row) => {
  const booking = row.booking || {};
  const paymentStatus = String(booking.paymentStatus || "").toLowerCase();
  const refundStatus = String(
    booking.refundStatus ||
      booking.refund?.status ||
      booking.refundRequest?.status ||
      ""
  ).toLowerCase();

  return (
    booking.refundRequested === true ||
    booking.refundRequest?.requested === true ||
    booking.refund?.requested === true ||
    paymentStatus === "refunded" ||
    paymentStatus === "partially_refunded" ||
    ["requested", "pending", "approved", "processing", "processed", "completed"].includes(refundStatus)
  );
};

const resolveMetadataStatus = (booking) => {
  const orderNumber = resolveOrderNumber(booking);
  const fromStripeVerification =
    booking.paymentVerification?.stripeOrderReference ||
    booking.paymentVerification?.orderReference;
  const fromRawPayment =
    booking.rawPayment?.paymentIntent?.metadata?.orderNumber ||
    booking.rawPayment?.paymentIntent?.metadata?.bookingId;
  const metadataReference = fromStripeVerification || fromRawPayment || "";

  if (!metadataReference) {
    return { label: "Metadata Missing", color: "warning" };
  }

  const isMatch = String(metadataReference) === String(orderNumber);
  if (isMatch) {
    return { label: "Metadata Linked", color: "success" };
  }

  return { label: "Metadata Mismatch", color: "error" };
};

const resolveTracker = (booking) => {
  const invoiceNumber = resolveInvoiceNumber(booking);
  const invoiceDelivery = resolveInvoiceDelivery(booking);
  const collectionReconciled =
    booking.collectionReconciled === true ||
    booking.bankReconciliation?.reconciled === true;
  const commissionPaid =
    booking.commissionPaid === true ||
    booking.payout?.paid === true ||
    Boolean(booking.payoutReferenceNumber || booking.transferId);
  const profitBooked =
    booking.profitBooked === true || (collectionReconciled && commissionPaid);

  const steps = [
    { key: "order", label: "Order Generated", done: true },
    {
      key: "invoice",
      label: invoiceDelivery.failed
        ? invoiceNumber
          ? `Invoice Failed (${invoiceNumber})`
          : "Invoice Failed"
        : invoiceDelivery.sent
        ? invoiceNumber
          ? `Invoice Sent (${invoiceNumber})`
          : "Invoice Sent"
        : invoiceDelivery.sending
        ? invoiceNumber
          ? `Invoice Sending (${invoiceNumber})`
          : "Invoice Sending"
        : invoiceNumber
        ? `Invoice Ready (${invoiceNumber})`
        : "Invoice Pending",
      done: invoiceDelivery.sent,
    },
    { key: "reconcile", label: "Collection Reconciled", done: collectionReconciled },
    { key: "commission", label: "Commission Paid", done: commissionPaid },
    { key: "profit", label: "Profit Booked", done: profitBooked },
  ];

  const completed = steps.filter((step) => step.done).length;
  return {
    steps,
    percent: Math.round((completed / steps.length) * 100),
  };
};

const PaymentLifecycle = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("lifecycle");
  const [lifecyclePage, setLifecyclePage] = useState(0);
  const [lifecycleRowsPerPage, setLifecycleRowsPerPage] = useState(10);
  const [reconciliationPage, setReconciliationPage] = useState(0);
  const [reconciliationRowsPerPage, setReconciliationRowsPerPage] = useState(10);
  const [reconciledPage, setReconciledPage] = useState(0);
  const [reconciledRowsPerPage, setReconciledRowsPerPage] = useState(10);
  const [selectedPayoutProviderId, setSelectedPayoutProviderId] = useState("");
  const [defaultCommissionRate, setDefaultCommissionRate] = useState(20);
  const [providerCommissionRates, setProviderCommissionRates] = useState({});
  const [providerProfiles, setProviderProfiles] = useState({});
  const [platformFeePerOrder, setPlatformFeePerOrder] = useState(PLATFORM_FEE_PER_ORDER);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [actionLoading, setActionLoading] = useState({});

  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualBooking, setManualBooking] = useState(null);
  const [manualForm, setManualForm] = useState({
    paymentReference: "",
    amount: "",
    notes: "",
  });

  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileBooking, setReconcileBooking] = useState(null);
  const [reconcileForm, setReconcileForm] = useState({
    bankDepositReference: "",
    actualDepositAmount: "",
    finalCharges: "",
    notes: "",
  });

  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutBooking, setPayoutBooking] = useState(null);
  const [payoutForm, setPayoutForm] = useState({
    amount: "",
    notes: "",
  });

  useEffect(() => {
    let isMounted = true;

    const loadReferenceData = async () => {
      try {
        const [platformSettingsSnap, providersSnap] = await Promise.all([
          getDoc(doc(db, "platformSettings", "main")),
          getDocs(
            query(collection(db, "users"), where("roles.client", "==", true))
          ),
        ]);

        if (!isMounted) return;

        if (platformSettingsSnap.exists()) {
          const settings = platformSettingsSnap.data() || {};
          const loadedCommission = asNumber(settings.defaultCommissionRate, 20);
          setDefaultCommissionRate(loadedCommission);

          if (settings.platformFeeType === "fixed") {
            const configuredFee = asNumber(settings.platformFee, PLATFORM_FEE_PER_ORDER);
            setPlatformFeePerOrder(configuredFee > 0 ? configuredFee : PLATFORM_FEE_PER_ORDER);
          }
        }

        const mappedRates = {};
        const mappedProfiles = {};
        providersSnap.forEach((providerDoc) => {
          const data = providerDoc.data() || {};
          mappedProfiles[providerDoc.id] = data;
          if (data.commissionRate != null) {
            mappedRates[providerDoc.id] = asNumber(
              data.commissionRate,
              defaultCommissionRate
            );
          }
        });
        setProviderCommissionRates(mappedRates);
        setProviderProfiles(mappedProfiles);
      } catch (loadErr) {
        console.error("Error loading platform/provider references:", loadErr);
      }
    };

    loadReferenceData();

    return () => {
      isMounted = false;
    };
  }, [defaultCommissionRate]);

  useEffect(() => {
    setLoading(true);
    setError("");

    const bookingsQuery = query(
      collection(db, "bookings"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      bookingsQuery,
      (snapshot) => {
        const items = snapshot.docs.map((bookingDoc) => ({
          id: bookingDoc.id,
          ...bookingDoc.data(),
        }));
        setBookings(items);
        setLoading(false);
      },
      (snapshotErr) => {
        console.error("Error loading payment lifecycle bookings:", snapshotErr);
        setError("Failed to fetch booking payment lifecycle data.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const lifecycleRows = useMemo(() => {
    return bookings.map((booking) => {
      const orderNumber = resolveOrderNumber(booking);
      const clientPayment = resolveClientPayment(booking);
      const invoiceNumber = resolveInvoiceNumber(booking);
      const invoiceAmount = resolveInvoiceAmount(booking, clientPayment);
      const invoiceUrl = resolveInvoiceUrl(booking);
      const invoiceDelivery = resolveInvoiceDelivery(booking);
      const providerInvoiceDelivery = resolveProviderInvoiceDelivery(booking);
      const gatewayEstimate = resolveGatewayChargeEstimate(booking, clientPayment);
      const finalCharges = resolveFinalCharges(booking, gatewayEstimate);
      const netCredited = resolveNetCredited(booking, clientPayment, finalCharges);

      const preliminaryServiceAmount = asNumber(
        booking.serviceTotal ?? booking.price ?? booking.basePrice,
        NaN
      );
      const platformFee = resolvePlatformFee(
        booking,
        clientPayment,
        Number.isFinite(preliminaryServiceAmount) ? preliminaryServiceAmount : clientPayment,
        platformFeePerOrder
      );
      const serviceBaseAmount = resolveServiceAmount(
        booking,
        clientPayment,
        platformFee
      );
      const serviceGst = gstIncluded(serviceBaseAmount);
      const commissionRate = resolveCommissionRate(
        booking,
        providerCommissionRates,
        defaultCommissionRate
      );
      const commissionAmount = resolveCommissionAmount(
        booking,
        serviceBaseAmount,
        commissionRate
      );
      const providerFee = resolveProviderFee(
        booking,
        clientPayment,
        commissionAmount,
        platformFee
      );

      const paymentReference =
        booking.chargeId ||
        booking.paymentIntentId ||
        booking.stripePaymentIntentId ||
        booking.manualPaymentReference ||
        "N/A";
      const provider = resolveProviderDisplay(booking, providerProfiles);

      return {
        booking,
        orderNumber,
        provider,
        clientPayment,
        paymentReference,
        invoiceNumber,
        invoiceAmount,
        invoiceUrl,
        invoiceDelivery,
        providerInvoiceDelivery,
        gatewayEstimate,
        finalCharges,
        netCredited,
        providerFee,
        commissionAmount,
        platformFee,
        serviceBaseAmount,
        gstIncluded: serviceGst.gst,
        netServicePrice: serviceGst.net,
        verificationStatus: resolveStripeVerificationStatus(booking),
        metadataStatus: resolveMetadataStatus(booking),
        tracker: resolveTracker(booking),
      };
    });
  }, [
    bookings,
    providerCommissionRates,
    providerProfiles,
    defaultCommissionRate,
    platformFeePerOrder,
  ]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return lifecycleRows;

    return lifecycleRows.filter((row) => {
      const b = row.booking;
      const values = [
        row.orderNumber,
        b.customerName,
        b.customerEmail,
        b.serviceName,
        row.provider.name,
        row.provider.email,
        row.provider.id,
        row.paymentReference,
        row.invoiceNumber,
        row.invoiceDelivery.label,
        row.invoiceDelivery.emailTo,
      ];

      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [lifecycleRows, searchTerm]);

  const reconciliationRows = useMemo(() => {
    return filteredRows.filter(
      (row) =>
        !isReconciledBooking(row)
    );
  }, [filteredRows]);

  const reconciledRows = useMemo(() => {
    return filteredRows.filter((row) => isReconciledBooking(row));
  }, [filteredRows]);

  const reconciliationSourceRows = useMemo(() => {
    return reconciliationRows.length ? reconciliationRows : filteredRows;
  }, [reconciliationRows, filteredRows]);

  const reconciledSourceRows = useMemo(() => {
    return reconciledRows;
  }, [reconciledRows]);

  const paginatedLifecycleRows = useMemo(() => {
    const start = lifecyclePage * lifecycleRowsPerPage;
    return filteredRows.slice(start, start + lifecycleRowsPerPage);
  }, [filteredRows, lifecyclePage, lifecycleRowsPerPage]);

  const paginatedReconciliationRows = useMemo(() => {
    const start = reconciliationPage * reconciliationRowsPerPage;
    return reconciliationSourceRows.slice(start, start + reconciliationRowsPerPage);
  }, [reconciliationSourceRows, reconciliationPage, reconciliationRowsPerPage]);

  const paginatedReconciledRows = useMemo(() => {
    const start = reconciledPage * reconciledRowsPerPage;
    return reconciledSourceRows.slice(start, start + reconciledRowsPerPage);
  }, [reconciledSourceRows, reconciledPage, reconciledRowsPerPage]);

  const providerPayoutOptions = useMemo(() => {
    const providers = new Map();
    lifecycleRows.forEach((row) => {
      if (!row.provider.id) return;
      providers.set(row.provider.id, row.provider);
    });
    return Array.from(providers.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  }, [lifecycleRows]);

  useEffect(() => {
    if (!selectedPayoutProviderId && providerPayoutOptions.length) {
      setSelectedPayoutProviderId(providerPayoutOptions[0].id);
    }
  }, [providerPayoutOptions, selectedPayoutProviderId]);

  const providerPayoutRows = useMemo(() => {
    if (!selectedPayoutProviderId) return [];
    return lifecycleRows.filter((row) =>
      row.provider.id === selectedPayoutProviderId &&
      isBookingComplete(row) &&
      isPaymentVerified(row) &&
      !isPayoutConfirmed(row) &&
      !hasRefundActivity(row)
    );
  }, [lifecycleRows, selectedPayoutProviderId]);

  const providerPaidRows = useMemo(() => {
    if (!selectedPayoutProviderId) return [];
    return lifecycleRows
      .filter((row) =>
        row.provider.id === selectedPayoutProviderId &&
        isPayoutConfirmed(row)
      )
      .sort((a, b) => {
        const aDate = a.booking.payoutDate?.toDate?.() || new Date(a.booking.payoutDate || 0);
        const bDate = b.booking.payoutDate?.toDate?.() || new Date(b.booking.payoutDate || 0);
        return bDate - aDate;
      });
  }, [lifecycleRows, selectedPayoutProviderId]);

  const providerPayoutTotal = useMemo(() =>
    providerPayoutRows.reduce((sum, row) => sum + asNumber(row.providerFee, 0), 0),
  [providerPayoutRows]);

  const providerPaidTotal = useMemo(() =>
    providerPaidRows.reduce((sum, row) => sum + asNumber(row.providerFee, 0), 0),
  [providerPaidRows]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredRows.length / lifecycleRowsPerPage) - 1
    );

    if (lifecyclePage > maxPage) {
      setLifecyclePage(maxPage);
    }
  }, [filteredRows.length, lifecycleRowsPerPage, lifecyclePage]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(reconciliationSourceRows.length / reconciliationRowsPerPage) - 1
    );

    if (reconciliationPage > maxPage) {
      setReconciliationPage(maxPage);
    }
  }, [
    reconciliationSourceRows.length,
    reconciliationRowsPerPage,
    reconciliationPage,
  ]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(reconciledSourceRows.length / reconciledRowsPerPage) - 1
    );

    if (reconciledPage > maxPage) {
      setReconciledPage(maxPage);
    }
  }, [
    reconciledSourceRows.length,
    reconciledRowsPerPage,
    reconciledPage,
  ]);

  const setLoadingState = (key, value) => {
    setActionLoading((prev) => ({ ...prev, [key]: value }));
  };

  const handleLifecyclePageChange = (_, nextPage) => {
    setLifecyclePage(nextPage);
  };

  const handleLifecycleRowsPerPageChange = (event) => {
    setLifecycleRowsPerPage(parseInt(event.target.value, 10));
    setLifecyclePage(0);
  };

  const handleReconciliationPageChange = (_, nextPage) => {
    setReconciliationPage(nextPage);
  };

  const handleReconciliationRowsPerPageChange = (event) => {
    setReconciliationRowsPerPage(parseInt(event.target.value, 10));
    setReconciliationPage(0);
  };

  const handleReconciledPageChange = (_, nextPage) => {
    setReconciledPage(nextPage);
  };

  const handleReconciledRowsPerPageChange = (event) => {
    setReconciledRowsPerPage(parseInt(event.target.value, 10));
    setReconciledPage(0);
  };

  const openSummaryTab = (tabValue) => {
    setActiveTab(tabValue);

    if (tabValue === "lifecycle") {
      setLifecyclePage(0);
    }

    if (tabValue === "reconciliation") {
      setReconciliationPage(0);
    }

    if (tabValue === "reconciled") {
      setReconciledPage(0);
    }
  };

  const confirmSelectedProviderPayout = async () => {
    if (!selectedPayoutProviderId || !providerPayoutRows.length) {
      showSnackbar("No unpaid completed bookings found for this provider.", "warning");
      return;
    }

    const key = `provider-payout-${selectedPayoutProviderId}`;
    setLoadingState(key, true);
    try {
      const reference = buildPayoutReference();
      const payload = await callAdminFunction(
        "confirmProviderBatchPayout",
        {
          providerId: selectedPayoutProviderId,
          bookingIds: providerPayoutRows.map((row) => row.booking.id),
          paymentReferenceNumber: reference,
          notes: "Admin provider payout from Payment Lifecycle dashboard",
        },
        currentUser,
      );

      showSnackbar(
        payload?.message || `Provider payout completed. Reference: ${payload?.payoutReferenceNumber || reference}`,
        "success",
      );
    } catch (payoutErr) {
      console.error("confirmSelectedProviderPayout error:", payoutErr);
      showSnackbar(payoutErr?.message || "Failed to process provider payout.", "error");
    } finally {
      setLoadingState(key, false);
    }
  };

  const resendProviderPaidStatement = async () => {
    if (!selectedPayoutProviderId || !providerPaidRows.length) {
      showSnackbar("No paid bookings found for this provider.", "warning");
      return;
    }

    const key = `provider-statement-${selectedPayoutProviderId}`;
    setLoadingState(key, true);
    try {
      await callAdminFunction(
        "sendProviderPayoutStatementManually",
        {
          providerId: selectedPayoutProviderId,
          bookingIds: providerPaidRows.map((row) => row.booking.id),
        },
        currentUser,
      );
      showSnackbar("Provider invoice resent.", "success");
    } catch (statementErr) {
      console.error("resendProviderPaidStatement error:", statementErr);
      showSnackbar(statementErr?.message || "Failed to resend provider invoice.", "error");
    } finally {
      setLoadingState(key, false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const verifyPaymentWithStripe = async (row) => {
    if (isPaymentVerified(row)) {
      showSnackbar("Stripe payment is already verified.", "info");
      return;
    }

    if (!hasStripeReference(row)) {
      showSnackbar(
        "Stripe transaction reference is missing for this order. Use manual confirmation or update the booking payment reference.",
        "warning"
      );
      return;
    }

    const bookingId = row.booking.id;
    const key = `verify-${bookingId}`;
    setLoadingState(key, true);

    try {
      const response = await fetch(`${CLOUD_FUNCTION_BASE}/verifyBookingPaymentStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingId }),
      });

      const payload = await response.json();
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || "Stripe verification failed.");
      }

      showSnackbar(
        payload?.message || "Stripe payment status synced successfully.",
        "success"
      );
    } catch (verifyErr) {
      console.error("verifyPaymentWithStripe error:", verifyErr);
      const message = String(verifyErr.message || "");
      const unableToResolve = message
        .toLowerCase()
        .includes("unable to resolve stripe transaction");

      showSnackbar(
        unableToResolve
          ? "Stripe verification failed because the backend could not find a matching Stripe PaymentIntent/Charge/Session for this booking. Check the Stripe reference or use manual confirmation."
          : `Stripe verification failed. ${message || "Try manual confirmation."}`,
        "error"
      );
    } finally {
      setLoadingState(key, false);
    }
  };

  const openManualPaymentDialog = (row) => {
    setManualBooking(row);
    setManualForm({
      paymentReference: row.booking.manualPaymentReference || "",
      amount: row.clientPayment ? String(row.clientPayment.toFixed(2)) : "",
      notes: row.booking.manualPaymentNotes || "",
    });
    setManualDialogOpen(true);
  };

  const saveManualPayment = async () => {
    if (!manualBooking?.booking?.id) return;

    const paymentReference = manualForm.paymentReference.trim();
    if (!paymentReference) {
      showSnackbar("Manual payment reference is required.", "error");
      return;
    }

    const amount = asNumber(manualForm.amount, NaN);
    const bookingId = manualBooking.booking.id;
    const key = `manual-${bookingId}`;
    setLoadingState(key, true);

    try {
      const response = await fetch(`${CLOUD_FUNCTION_BASE}/markBookingPaymentManually`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId,
          paymentReference,
          amount: Number.isFinite(amount) ? amount : undefined,
          notes: manualForm.notes,
        }),
      });

      const payload = await response.json();
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || "Manual payment confirmation failed.");
      }

      showSnackbar("Manual payment confirmation saved.", "success");
      setManualDialogOpen(false);
      setManualBooking(null);
    } catch (manualErr) {
      console.error("saveManualPayment error:", manualErr);
      showSnackbar(manualErr.message || "Failed to confirm manual payment.", "error");
    } finally {
      setLoadingState(key, false);
    }
  };

  const openReconcileDialog = (row) => {
    setReconcileBooking(row);
    setReconcileForm({
      bankDepositReference:
        row.booking.bankReconciliation?.bankDepositReference || "",
      actualDepositAmount:
        row.booking.bankReconciliation?.actualDepositAmount != null
          ? String(row.booking.bankReconciliation.actualDepositAmount)
          : String(row.netCredited.toFixed(2)),
      finalCharges:
        row.booking.bankReconciliation?.finalCharges != null
          ? String(row.booking.bankReconciliation.finalCharges)
          : String(row.finalCharges.toFixed(2)),
      notes: row.booking.bankReconciliation?.notes || "",
    });
    setReconcileDialogOpen(true);
  };

  const saveReconciliation = async () => {
    if (!reconcileBooking?.booking?.id) return;

    const bookingId = reconcileBooking.booking.id;
    const actualDepositAmount = asNumber(reconcileForm.actualDepositAmount, NaN);
    const finalCharges = asNumber(reconcileForm.finalCharges, NaN);

    if (!Number.isFinite(actualDepositAmount) || actualDepositAmount < 0) {
      showSnackbar("Please enter a valid bank deposit amount.", "error");
      return;
    }

    if (!Number.isFinite(finalCharges) || finalCharges < 0) {
      showSnackbar("Please enter valid final bank charges.", "error");
      return;
    }

    const key = `reconcile-${bookingId}`;
    setLoadingState(key, true);

    try {
      await updateDoc(doc(db, "bookings", bookingId), {
        bankReconciliation: {
          reconciled: true,
          reconciledAt: serverTimestamp(),
          reconciledBy: "admin",
          bankDepositReference: reconcileForm.bankDepositReference.trim(),
          actualDepositAmount,
          finalCharges,
          notes: reconcileForm.notes.trim(),
        },
        collectionReconciled: true,
        bankStripeChargesFinal: finalCharges,
        netAmountCreditedToBank: actualDepositAmount,
        updatedAt: serverTimestamp(),
      });

      showSnackbar("Bank reconciliation completed.", "success");
      setReconcileDialogOpen(false);
      setReconcileBooking(null);
    } catch (reconcileErr) {
      console.error("saveReconciliation error:", reconcileErr);
      showSnackbar("Failed to save reconciliation.", "error");
    } finally {
      setLoadingState(key, false);
    }
  };

  const openPayoutDialog = (row) => {
    if (isPayoutConfirmed(row)) {
      showSnackbar("Provider payout is already confirmed.", "info");
      return;
    }

    if (hasRefundActivity(row)) {
      showSnackbar("Provider payout is disabled while a refund is active.", "warning");
      return;
    }

    if (!hasStripeReference(row)) {
      showSnackbar(
        "No Stripe payment reference was found for this booking, so provider payout cannot be transferred yet.",
        "warning"
      );
      return;
    }

    if (!Number.isFinite(row.providerFee) || row.providerFee <= 0) {
      showSnackbar("No provider payout is available for this booking.", "warning");
      return;
    }

    setPayoutBooking(row);
    setPayoutForm({
      amount:
        row.booking.payout?.amount != null
          ? String(row.booking.payout.amount)
          : String(row.providerFee.toFixed(2)),
      notes: row.booking.payout?.notes || "",
    });
    setPayoutDialogOpen(true);
  };

  const confirmPayout = async () => {
    if (!payoutBooking?.booking?.id) return;

    if (isPayoutConfirmed(payoutBooking)) {
      showSnackbar("Provider payout is already confirmed.", "info");
      setPayoutDialogOpen(false);
      setPayoutBooking(null);
      return;
    }

    if (hasRefundActivity(payoutBooking)) {
      showSnackbar("Provider payout is disabled while a refund is active.", "warning");
      setPayoutDialogOpen(false);
      setPayoutBooking(null);
      return;
    }

    const bookingId = payoutBooking.booking.id;
    const payoutAmount = asNumber(payoutForm.amount, NaN);
    if (!Number.isFinite(payoutAmount) || payoutAmount <= 0) {
      showSnackbar("Please enter a valid payout amount.", "error");
      return;
    }

    const paymentReferenceNumber = buildPayoutReference();
    const key = `payout-${bookingId}`;
    setLoadingState(key, true);

    try {
      const payload = await callAdminFunction(
        "confirmProviderPayout",
        {
          bookingId,
          amount: payoutAmount,
          notes: payoutForm.notes.trim(),
          paymentReferenceNumber,
        },
        currentUser,
      );

      showSnackbar(
        payload?.message ||
          `Payout transferred. Reference: ${payload?.payoutReferenceNumber || paymentReferenceNumber}`,
        "success"
      );
      setPayoutDialogOpen(false);
      setPayoutBooking(null);
    } catch (payoutErr) {
      console.error("confirmPayout error:", payoutErr);
      showSnackbar("Failed to confirm payout.", "error");
    } finally {
      setLoadingState(key, false);
    }
  };

  const markProfitBooked = async (row) => {
    if (isProfitBooked(row)) {
      showSnackbar("Profit is already booked for this order.", "info");
      return;
    }

    if (hasRefundActivity(row)) {
      showSnackbar("Profit booking is disabled while a refund is active.", "warning");
      return;
    }

    const bookingId = row.booking.id;
    const key = `profit-${bookingId}`;
    setLoadingState(key, true);

    try {
      await updateDoc(doc(db, "bookings", bookingId), {
        profitBooked: true,
        profitBookedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      showSnackbar("Profit marked as booked.", "success");
    } catch (profitErr) {
      console.error("markProfitBooked error:", profitErr);
      showSnackbar("Failed to mark profit as booked.", "error");
    } finally {
      setLoadingState(key, false);
    }
  };

  const sendInvoiceFromAdmin = async (row, target) => {
    if (!isBookingComplete(row)) {
      showSnackbar("Complete the booking before sending invoices.", "warning");
      return;
    }

    const bookingId = row.booking.id;
    const key = `${target}-invoice-send-${bookingId}`;
    setLoadingState(key, true);

    try {
      await callAdminFunction(
        "sendBookingInvoiceManually",
        {
          bookingId,
          target,
        },
        currentUser,
      );
      showSnackbar(
        target === "provider"
          ? "Provider invoice sent."
          : target === "customer"
          ? "Customer invoice sent."
          : "Invoices sent.",
        "success"
      );
    } catch (invoiceErr) {
      console.error("sendInvoiceFromAdmin error:", invoiceErr);
      showSnackbar(invoiceErr?.message || "Failed to send invoice.", "error");
    } finally {
      setLoadingState(key, false);
    }
  };

  const openRefundFlow = (row) => {
    if (isBookingComplete(row) && isPayoutConfirmed(row)) {
      showSnackbar("Refund is disabled after payout is confirmed for a completed booking.", "warning");
      return;
    }

    navigate("/dashboard/refunds", {
      state: {
        bookingId: row.booking.id,
        source: "payment-lifecycle",
      },
    });
  };

  const openProviderProfile = (row) => {
    const providerId = row.booking.providerId || row.booking.clientId;
    if (!providerId) {
      showSnackbar("No provider is assigned to this order.", "warning");
      return;
    }

    navigate("/dashboard/clients", {
      state: {
        openProviderId: providerId,
        source: "payment-lifecycle",
      },
    });
  };

  const lifeCycleSummary = useMemo(() => {
    const totalOrders = filteredRows.length;
    const verifiedCount = filteredRows.filter(
      (row) => row.verificationStatus.label === "Verified"
    ).length;
    const reconciledCount = filteredRows.filter((row) => isReconciledBooking(row)).length;
    const payoutDoneCount = filteredRows.filter(
      (row) => row.booking.payout?.paid === true || row.booking.commissionPaid === true
    ).length;

    return {
      totalOrders,
      verifiedCount,
      reconciledCount,
      payoutDoneCount,
    };
  }, [filteredRows]);

  const renderStatusTracker = (row) => {
    return (
      <Stack spacing={0.8} sx={{ minWidth: 250 }}>
        <LinearProgress
          variant="determinate"
          value={row.tracker.percent}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" color="text.secondary">
          {row.tracker.percent}% complete
        </Typography>
        <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
          {row.tracker.steps.map((step) => (
            <Chip
              key={step.key}
              label={step.label}
              size="small"
              color={step.done ? "success" : "default"}
              variant={step.done ? "filled" : "outlined"}
            />
          ))}
        </Stack>
      </Stack>
    );
  };

  const PanelHeader = ({ icon, title, chip }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            bgcolor: "transparent",
            color: "primary.main",
            flex: "0 0 auto",
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 500, color: "text.primary" }}>
          {title}
        </Typography>
      </Stack>
      {chip}
    </Stack>
  );

  const DetailLine = ({ label, value, strong = false, wrap = false }) => (
    <Stack
      direction={wrap ? "column" : "row"}
      justifyContent="space-between"
      spacing={wrap ? 0.15 : 1.5}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: strong ? 600 : 400,
          textAlign: wrap ? "left" : "right",
          overflowWrap: wrap ? "anywhere" : "normal",
          minWidth: 0,
          color: strong ? "text.primary" : "text.primary",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );

  const ContactLine = ({ label, value }) => (
    <Stack spacing={0.1} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, textTransform: "uppercase", fontSize: 11 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: value ? "text.primary" : "text.secondary",
          fontWeight: 400,
          overflowWrap: "anywhere",
        }}
      >
        {value || "Missing"}
      </Typography>
    </Stack>
  );

  if (loading) {
    return (
      <Box p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, overflowX: "hidden" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 1, maxWidth: "100%" }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                icon={<ReceiptLongIcon />}
                label={`Orders: ${lifeCycleSummary.totalOrders}`}
                color="primary"
                variant="outlined"
                clickable
                onClick={() => openSummaryTab("lifecycle")}
                sx={{ ...summaryChipSx, cursor: "pointer" }}
              />
              <Chip
                icon={<VerifiedIcon />}
                label={`Stripe Verified: ${lifeCycleSummary.verifiedCount}`}
                color="success"
                variant="outlined"
                clickable
                onClick={() => openSummaryTab("status")}
                sx={{ ...summaryChipSx, cursor: "pointer" }}
              />
              <Chip
                icon={<AccountBalanceIcon />}
                label={`Reconciled: ${lifeCycleSummary.reconciledCount}`}
                color="info"
                variant="outlined"
                clickable
                onClick={() => openSummaryTab("reconciled")}
                sx={{ ...summaryChipSx, cursor: "pointer" }}
              />
              <Chip
                icon={<AttachMoneyIcon />}
                label={`Commission Paid: ${lifeCycleSummary.payoutDoneCount}`}
                color="warning"
                variant="outlined"
                clickable
                onClick={() => openSummaryTab("status")}
                sx={{ ...summaryChipSx, cursor: "pointer" }}
              />
            </Stack>

            <TextField
              size="small"
              label="Search orders"
              placeholder="Order, customer, provider, invoice"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              variant="outlined"
              sx={{
                minWidth: { xs: "100%", md: 340 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                },
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, maxWidth: "100%" }}
      >
        <Tab value="lifecycle" label="Payment Lifecycle" icon={<HistoryIcon />} iconPosition="start" />
        <Tab
          value="provider-payout"
          label="Provider Payout"
          icon={<PaymentsOutlinedIcon />}
          iconPosition="start"
        />
        <Tab
          value="reconciliation"
          label="Bank Reconciliation"
          icon={<AccountBalanceIcon />}
          iconPosition="start"
        />
        <Tab
          value="reconciled"
          label="Reconciled List"
          icon={<CheckCircleOutlineIcon />}
          iconPosition="start"
        />
        <Tab
          value="status"
          label="Status Tracker"
          icon={<CheckCircleOutlineIcon />}
          iconPosition="start"
        />
      </Tabs>

      {activeTab === "lifecycle" && (
        <Paper
          variant="outlined"
          sx={{
            maxWidth: "100%",
            overflow: "hidden",
            borderRadius: 2,
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Stack spacing={1.5} sx={{ p: { xs: 1.25, md: 2 } }}>
            {paginatedLifecycleRows.length === 0 ? (
              <Alert severity="info">No lifecycle records found for current filters.</Alert>
            ) : (
              paginatedLifecycleRows.map((row) => {
                const b = row.booking;
                const provider = row.provider;
                const payoutReference =
                  b.payout?.paymentReferenceNumber || b.payoutReferenceNumber || "";
                const paymentVerified = isPaymentVerified(row);
                const payoutConfirmed = isPayoutConfirmed(row);
                const profitBooked = isProfitBooked(row);
                const bookingComplete = isBookingComplete(row);
                const refundActive = hasRefundActivity(row);
                const refundLocked = bookingComplete && payoutConfirmed;

                return (
                  <Card
                    key={b.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: "divider",
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                    }}
                  >
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                      <Stack spacing={0}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.5}
                          alignItems={{ xs: "stretch", md: "flex-start" }}
                          justifyContent="space-between"
                          sx={{ p: 2 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600, overflowWrap: "anywhere", color: "text.primary" }}
                            >
                              {row.orderNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Created {dateLabel(b.createdAt)}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip
                              size="small"
                              color={row.verificationStatus.color}
                              label={row.verificationStatus.label}
                            />
                            <Chip
                              size="small"
                              color={row.metadataStatus.color}
                              label={row.metadataStatus.label}
                              variant="outlined"
                            />
                          </Stack>
                        </Stack>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                              lg: "repeat(4, minmax(0, 1fr))",
                            },
                            alignItems: "stretch",
                            gap: 1.5,
                            px: 2,
                            pb: 2,
                          }}
                        >
                          <Box sx={paymentPanelSx}>
                            <PanelHeader
                              icon={<AttachMoneyIcon fontSize="small" />}
                              title="Client Payment"
                              chip={
                                <Chip
                                  size="small"
                                  label={money(row.clientPayment)}
                                  sx={amountBadgeSx}
                                />
                              }
                            />
                            <Stack spacing={0.55} sx={{ mt: 1.25 }}>
                              <DetailLine label="Service fee" value={money(row.serviceBaseAmount)} />
                              <DetailLine label="Platform fee" value={money(row.platformFee)} />
                              <DetailLine label="GST included" value={money(row.gstIncluded)} />
                              <DetailLine label="Net service" value={money(row.netServicePrice)} />
                              <Divider sx={{ my: 0.25 }} />
                              <DetailLine label="Stripe ref" value={row.paymentReference} wrap />
                            </Stack>
                          </Box>

                          <Box sx={paymentPanelSx}>
                            <PanelHeader
                              icon={<PersonOutlineIcon fontSize="small" />}
                              title="Parties"
                              chip={
                                <Chip
                                  size="small"
                                  label={provider.id ? "Assigned" : "Missing"}
                                  sx={{
                                    fontWeight: 500,
                                    borderRadius: 999,
                                    textTransform: "uppercase",
                                  }}
                                  color={provider.id ? "success" : "warning"}
                                  variant="outlined"
                                />
                              }
                            />
                            <Box sx={{ mt: 1.1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "primary.main",
                                  fontWeight: 500,
                                  mb: 0.35,
                                }}
                              >
                                Provider
                              </Typography>
                              {provider.id ? (
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() => openProviderProfile(row)}
                                  sx={{
                                    display: "block",
                                    justifyContent: "flex-start",
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    p: 0,
                                    textAlign: "left",
                                    textTransform: "none",
                                  }}
                                >
                                  <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: "primary.main" }}>
                                    {provider.name}
                                  </Typography>
                                </Button>
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  Unassigned
                                </Typography>
                              )}
                              {provider.email && (
                                <ContactLine label="Provider email" value={provider.email} />
                              )}
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "primary.main",
                                  fontWeight: 500,
                                  mb: 0.35,
                                }}
                              >
                                Customer
                              </Typography>
                              <ContactLine
                                label="Customer email"
                                value={row.invoiceDelivery.emailTo || b.customerEmail || b.email}
                              />
                            </Box>
                          </Box>

                          <Box sx={paymentPanelSx}>
                            <PanelHeader
                              icon={<ReceiptLongIcon fontSize="small" />}
                              title="Invoice Actions"
                            />
                            <Stack spacing={0.9} sx={{ mt: 1.15 }}>
                              <Stack spacing={0.55}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                      Customer invoice
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={row.invoiceDelivery.label.replace("Invoice ", "")}
                                    sx={statusBadgeSx(row.invoiceDelivery.sent ? "success" : "warning")}
                                  />
                                </Stack>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                                  >
                                    {row.invoiceUrl ? (
                                      <Button
                                        component="a"
                                        href={row.invoiceUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        size="small"
                                        variant="text"
                                        sx={{ textTransform: "none", p: 0, minWidth: 0, fontSize: 12 }}
                                      >
                                        {row.invoiceNumber || "Open invoice"}
                                      </Button>
                                    ) : (
                                      row.invoiceNumber || row.orderNumber
                                    )}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, flex: "0 0 auto" }}>
                                    {money(row.invoiceAmount || row.clientPayment)}
                                  </Typography>
                                </Stack>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EmailOutlinedIcon fontSize="small" />}
                                  sx={{ ...invoiceActionButtonSx, width: "100%" }}
                                  onClick={() => sendInvoiceFromAdmin(row, "customer")}
                                  disabled={
                                    !bookingComplete ||
                                    Boolean(actionLoading[`customer-invoice-send-${b.id}`])
                                  }
                                >
                                  {!bookingComplete
                                    ? "Complete booking first"
                                    : actionLoading[`customer-invoice-send-${b.id}`]
                                    ? "Sending..."
                                    : row.invoiceDelivery.sent
                                    ? "Resend to customer"
                                    : "Send to customer"}
                                </Button>
                              </Stack>

                              <Divider />

                              <Stack spacing={0.55}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                      Provider advice
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={row.providerInvoiceDelivery.label.replace("Provider Invoice ", "")}
                                    sx={statusBadgeSx(row.providerInvoiceDelivery.sent ? "success" : "warning")}
                                  />
                                </Stack>
                                {row.providerInvoiceDelivery.holdReason && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", textTransform: "capitalize" }}>
                                    Hold: {row.providerInvoiceDelivery.holdReason.replaceAll("_", " ")}
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  variant="contained"
                                  color={row.providerInvoiceDelivery.sent ? "success" : "primary"}
                                  startIcon={<LockOpenOutlinedIcon fontSize="small" />}
                                  sx={{
                                    ...pillControlSx,
                                    width: "100%",
                                    mt: 0.75,
                                    minHeight: 36,
                                    borderRadius: 999,
                                    bgcolor: row.providerInvoiceDelivery.sent ? "success.main" : "primary.main",
                                    "&:hover": {
                                      bgcolor: row.providerInvoiceDelivery.sent ? "success.dark" : "primary.dark",
                                    },
                                  }}
                                  onClick={() => sendInvoiceFromAdmin(row, "provider")}
                                  disabled={
                                    !bookingComplete ||
                                    !payoutConfirmed ||
                                    row.providerInvoiceDelivery.sending ||
                                    Boolean(actionLoading[`provider-invoice-send-${b.id}`])
                                  }
                                >
                                  {!bookingComplete
                                    ? "Complete booking first"
                                    : !payoutConfirmed
                                    ? "Send after payout"
                                    : actionLoading[`provider-invoice-send-${b.id}`]
                                    ? "Sending..."
                                    : row.providerInvoiceDelivery.sent
                                    ? "Resend invoice"
                                    : "Send provider invoice"}
                                </Button>
                              </Stack>
                            </Stack>
                          </Box>

                          <Box sx={paymentPanelSx}>
                            <PanelHeader
                              icon={<PaymentsOutlinedIcon fontSize="small" />}
                              title="Payout"
                              chip={
                                <Chip
                                  size="small"
                                  label={payoutConfirmed ? "Paid" : "Review"}
                                  color={payoutConfirmed ? "success" : "warning"}
                                  variant={payoutConfirmed ? "filled" : "outlined"}
                                />
                              }
                            />
                            <Stack spacing={0.55} sx={{ mt: 1.25 }}>
                              <DetailLine label="Provider payout" value={money(row.providerFee)} strong />
                              <DetailLine label="MLF commission" value={money(row.commissionAmount)} />
                              <DetailLine label="Platform fee" value={money(row.platformFee)} />
                              <Typography variant="caption" color="text.secondary">
                                Payment processing fees are not deducted from provider payout.
                              </Typography>
                            </Stack>
                            {payoutReference && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", overflowWrap: "anywhere", mt: 0.75 }}
                              >
                                Ref: {payoutReference}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Divider />

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{
                            p: 2,
                            pt: 1.5,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            sx={pillControlSx}
                            onClick={() => verifyPaymentWithStripe(row)}
                            disabled={
                              paymentVerified ||
                              !hasStripeReference(row) ||
                              Boolean(actionLoading[`verify-${b.id}`])
                            }
                          >
                            {paymentVerified
                              ? "Verified"
                              : actionLoading[`verify-${b.id}`]
                              ? "Verifying..."
                              : hasStripeReference(row)
                              ? "Verify Stripe"
                              : "No Stripe Ref"}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            sx={pillControlSx}
                            onClick={() => openManualPaymentDialog(row)}
                            disabled={paymentVerified}
                          >
                            {paymentVerified ? "Confirmed" : "Manual Confirm"}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            sx={pillControlSx}
                            onClick={() => openRefundFlow(row)}
                            disabled={refundLocked}
                          >
                            {refundLocked ? "Refund Locked" : "Process Refund"}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            color="success"
                            startIcon={<PaymentsOutlinedIcon fontSize="small" />}
                            sx={pillControlSx}
                            onClick={() => openPayoutDialog(row)}
                            disabled={payoutConfirmed || refundActive}
                          >
                            {payoutConfirmed
                              ? "Payout Confirmed"
                              : refundActive
                              ? "Refund Active"
                              : "Confirm Payout"}
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => markProfitBooked(row)}
                            disabled={
                              profitBooked ||
                              refundActive ||
                              Boolean(actionLoading[`profit-${b.id}`])
                            }
                            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                          >
                            {profitBooked
                              ? "Profit Booked"
                              : refundActive
                              ? "Refund Active"
                              : "Book Profit"}
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Stack>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={lifecyclePage}
            onPageChange={handleLifecyclePageChange}
            rowsPerPage={lifecycleRowsPerPage}
            onRowsPerPageChange={handleLifecycleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20]}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          />
        </Paper>
      )}

      {activeTab === "reconciliation" && (
        <Paper variant="outlined" sx={{ maxWidth: "100%", overflow: "hidden" }}>
          <TableContainer
            sx={{
              overflow: "auto",
              maxHeight: { xs: 440, md: 600 },
              maxWidth: "100%",
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap" }}><strong>Order Number</strong></TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap" }}><strong>Client Payment</strong></TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap" }}><strong>Net Credited</strong></TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap" }}><strong>Reconciliation Status</strong></TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap" }}><strong>Reconciled At</strong></TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap" }}><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReconciliationRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                      >
                        No reconciliation records found for current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReconciliationRows.map((row) => {
                    const reconciled =
                      row.booking.collectionReconciled === true ||
                      row.booking.bankReconciliation?.reconciled === true;

                    return (
                      <TableRow key={`recon-${row.booking.id}`} hover>
                    <TableCell>{row.orderNumber}</TableCell>
                    <TableCell>{money(row.clientPayment)}</TableCell>
                    <TableCell>{money(row.netCredited)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={reconciled ? "success" : "warning"}
                        label={reconciled ? "Reconciled" : "Pending"}
                      />
                    </TableCell>
                    <TableCell>
                      {datetimeLabel(row.booking.bankReconciliation?.reconciledAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openReconcileDialog(row)}
                      >
                        {reconciled ? "Update Reconcile" : "Reconcile"}
                      </Button>
                    </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={reconciliationSourceRows.length}
            page={reconciliationPage}
            onPageChange={handleReconciliationPageChange}
            rowsPerPage={reconciliationRowsPerPage}
            onRowsPerPageChange={handleReconciliationRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 30]}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          />
        </Paper>
      )}

      {activeTab === "provider-payout" && (
        <Paper variant="outlined" sx={{ maxWidth: "100%", overflow: "hidden", borderRadius: 2 }}>
          <Stack spacing={2} sx={{ p: { xs: 1.25, md: 2 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Provider Payout
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Weekly auto payout runs every Monday after 2:00 AM. Admin can also review a provider and process payout manually.
                </Typography>
              </Box>

              <TextField
                select
                size="small"
                label="Provider"
                value={selectedPayoutProviderId}
                onChange={(event) => setSelectedPayoutProviderId(event.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: { xs: "100%", md: 320 } }}
              >
                {providerPayoutOptions.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name || provider.email || provider.id}
                  </option>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <Box sx={{ ...paymentPanelSx, flex: 1 }}>
                <PanelHeader
                  icon={<PaymentsOutlinedIcon fontSize="small" />}
                  title="Ready for Payout"
                  chip={<Chip size="small" label={money(providerPayoutTotal)} sx={amountBadgeSx} />}
                />
                <Stack spacing={0.4} sx={{ mt: 1 }}>
                  <DetailLine label="Bookings" value={providerPayoutRows.length} />
                  <DetailLine label="Invoice" value="One provider invoice after payout" />
                </Stack>
              </Box>

              <Box sx={{ ...paymentPanelSx, flex: 1 }}>
                <PanelHeader
                  icon={<ReceiptLongIcon fontSize="small" />}
                  title="Provider Statement"
                  chip={<Chip size="small" label={`${providerPaidRows.length} paid`} sx={statusBadgeSx("success")} />}
                />
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <DetailLine label="Paid total" value={money(providerPaidTotal)} />
                  <Typography variant="caption" color="text.secondary">
                    Resend creates one provider invoice from paid bookings in history.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={invoiceActionButtonSx}
                    onClick={resendProviderPaidStatement}
                    disabled={
                      !providerPaidRows.length ||
                      Boolean(actionLoading[`provider-statement-${selectedPayoutProviderId}`])
                    }
                  >
                    {actionLoading[`provider-statement-${selectedPayoutProviderId}`]
                      ? "Sending..."
                      : "Resend provider invoice"}
                  </Button>
                </Stack>
              </Box>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Booking</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Service fee</TableCell>
                    <TableCell align="right">MLF commission</TableCell>
                    <TableCell align="right">Provider payout</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providerPayoutRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Alert severity="info">No completed unpaid bookings are ready for this provider.</Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    providerPayoutRows.map((row) => (
                      <TableRow key={row.booking.id} hover>
                        <TableCell>{row.orderNumber}</TableCell>
                        <TableCell>{row.booking.serviceName || row.booking.subcategory || "Service"}</TableCell>
                        <TableCell>{row.booking.customerName || "Customer"}</TableCell>
                        <TableCell>{row.booking.selectedDate || "TBD"}</TableCell>
                        <TableCell align="right">{money(row.serviceBaseAmount)}</TableCell>
                        <TableCell align="right">{money(row.commissionAmount)}</TableCell>
                        <TableCell align="right">{money(row.providerFee)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<PaymentsOutlinedIcon fontSize="small" />}
                sx={pillControlSx}
                onClick={confirmSelectedProviderPayout}
                disabled={
                  !providerPayoutRows.length ||
                  Boolean(actionLoading[`provider-payout-${selectedPayoutProviderId}`])
                }
              >
                {actionLoading[`provider-payout-${selectedPayoutProviderId}`]
                  ? "Processing..."
                  : "Confirm Provider Payout"}
              </Button>
            </Stack>

            <Divider />

            <Stack spacing={1}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Previous payout history
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Paid bookings for the selected provider with invoice delivery status.
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${providerPaidRows.length} paid bookings`}
                  sx={statusBadgeSx("primary")}
                />
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Booking</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Payout date</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Statement</TableCell>
                      <TableCell align="right">Provider payout</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {providerPaidRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Alert severity="info">No previous payouts found for this provider.</Alert>
                        </TableCell>
                      </TableRow>
                    ) : (
                      providerPaidRows.map((row) => {
                        const invoiceSent = Boolean(row.booking.providerInvoiceSent);
                        const reference =
                          row.booking.providerInvoiceBatchPayoutReference ||
                          row.booking.payoutReferenceNumber ||
                          row.booking.payout?.paymentReferenceNumber ||
                          row.booking.payoutTransferId ||
                          "N/A";
                        return (
                          <TableRow key={`paid-${row.booking.id}`} hover>
                            <TableCell>{row.orderNumber}</TableCell>
                            <TableCell>{row.booking.serviceName || row.booking.subcategory || "Service"}</TableCell>
                            <TableCell>{datetimeLabel(row.booking.payoutDate)}</TableCell>
                            <TableCell>{reference}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Chip
                                  size="small"
                                  label={invoiceSent ? "Sent" : "Pending"}
                                  sx={statusBadgeSx(invoiceSent ? "success" : "warning")}
                                />
                                {row.booking.providerInvoiceSentAt && (
                                  <Typography variant="caption" color="text.secondary">
                                    {datetimeLabel(row.booking.providerInvoiceSentAt)}
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">{money(row.providerFee)}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        </Paper>
      )}

      {activeTab === "reconciled" && (
        <Paper variant="outlined" sx={{ maxWidth: "100%", overflow: "hidden", borderRadius: 2, boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)" }}>
          <TableContainer
            sx={{
              overflow: "auto",
              maxHeight: { xs: 440, md: 600 },
              maxWidth: "100%",
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap", fontWeight: 600 }}>Order Number</TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap", fontWeight: 600 }}>Provider</TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap", fontWeight: 600 }}>Client Payment</TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap", fontWeight: 600 }}>Net Credited</TableCell>
                  <TableCell sx={{ backgroundColor: "#f8fafc", whiteSpace: "nowrap", fontWeight: 600 }}>Reconciled At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReconciledRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ py: 2 }}
                      >
                        No reconciled records found for current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReconciledRows.map((row) => {
                    const b = row.booking;
                    const reconciledAt = b.bankReconciliation?.reconciledAt || b.collectionReconciledAt || b.updatedAt;

                    return (
                      <TableRow key={`reconciled-${b.id}`} hover>
                        <TableCell>{row.orderNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {row.provider.name}
                          </Typography>
                          {row.provider.email && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                              {row.provider.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{money(row.clientPayment)}</TableCell>
                        <TableCell>{money(row.netCredited)}</TableCell>
                        <TableCell>{datetimeLabel(reconciledAt)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={reconciledSourceRows.length}
            page={reconciledPage}
            onPageChange={handleReconciledPageChange}
            rowsPerPage={reconciledRowsPerPage}
            onRowsPerPageChange={handleReconciledRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20]}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          />
        </Paper>
      )}

      {activeTab === "status" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {filteredRows.length === 0 ? (
            <Alert severity="info">No lifecycle records found for current filters.</Alert>
          ) : (
            filteredRows.map((row) => {
              const b = row.booking;
              const provider = row.provider;
              const payoutReference =
                b.payout?.paymentReferenceNumber ||
                b.payoutReferenceNumber ||
                "Pending";

              return (
                <Card key={`status-${b.id}`} variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {row.orderNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Created: {dateLabel(b.createdAt)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={row.verificationStatus.color}
                          label={row.verificationStatus.label}
                        />
                      </Stack>

                      {renderStatusTracker(row)}

                      <Divider />

                      <Stack spacing={0.8}>
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            Invoice
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.invoiceNumber || "Pending"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            Provider
                          </Typography>
                          {provider.id ? (
                            <Stack spacing={0.2} alignItems="flex-end" sx={{ minWidth: 0 }}>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => openProviderProfile(row)}
                                sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                              >
                                {provider.name}
                              </Button>
                              {provider.email && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {provider.email}
                                </Typography>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2">Unassigned</Typography>
                          )}
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            Provider Payout
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {money(row.providerFee)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            Payment Reference
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, textAlign: "right", wordBreak: "break-all" }}
                          >
                            {payoutReference}
                          </Typography>
                        </Stack>
                      </Stack>

                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      )}

      <Dialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manual Payment Confirmation</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Order: {manualBooking?.orderNumber || "N/A"}
            </Typography>
            <TextField
              label="Manual Payment Reference"
              value={manualForm.paymentReference}
              onChange={(event) =>
                setManualForm((prev) => ({
                  ...prev,
                  paymentReference: event.target.value,
                }))
              }
              required
              fullWidth
            />
            <TextField
              label="Confirmed Amount"
              type="number"
              value={manualForm.amount}
              onChange={(event) =>
                setManualForm((prev) => ({ ...prev, amount: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Admin Notes"
              multiline
              minRows={3}
              value={manualForm.notes}
              onChange={(event) =>
                setManualForm((prev) => ({ ...prev, notes: event.target.value }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={saveManualPayment}
            variant="contained"
            disabled={Boolean(actionLoading[`manual-${manualBooking?.booking?.id}`])}
          >
            {actionLoading[`manual-${manualBooking?.booking?.id}`]
              ? "Saving..."
              : "Confirm Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reconcileDialogOpen}
        onClose={() => setReconcileDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bank Reconciliation</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Order: {reconcileBooking?.orderNumber || "N/A"}
            </Typography>
            <TextField
              label="Bank Deposit Reference"
              value={reconcileForm.bankDepositReference}
              onChange={(event) =>
                setReconcileForm((prev) => ({
                  ...prev,
                  bankDepositReference: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Actual Amount Credited to Bank"
              type="number"
              value={reconcileForm.actualDepositAmount}
              onChange={(event) =>
                setReconcileForm((prev) => ({
                  ...prev,
                  actualDepositAmount: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Final Bank Charges"
              type="number"
              value={reconcileForm.finalCharges}
              onChange={(event) =>
                setReconcileForm((prev) => ({
                  ...prev,
                  finalCharges: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Reconciliation Notes"
              multiline
              minRows={3}
              value={reconcileForm.notes}
              onChange={(event) =>
                setReconcileForm((prev) => ({ ...prev, notes: event.target.value }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={saveReconciliation}
            variant="contained"
            disabled={Boolean(actionLoading[`reconcile-${reconcileBooking?.booking?.id}`])}
          >
            {actionLoading[`reconcile-${reconcileBooking?.booking?.id}`]
              ? "Saving..."
              : "Save Reconciliation"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={payoutDialogOpen}
        onClose={() => setPayoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Provider Payout</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Order: {payoutBooking?.orderNumber || "N/A"}
            </Typography>
            <TextField
              label="Payout Amount"
              type="number"
              value={payoutForm.amount}
              InputProps={{ readOnly: true }}
              helperText="Calculated from client payment minus platform fee and MLF commission. Payment processing fees are not deducted."
              onChange={(event) =>
                setPayoutForm((prev) => ({ ...prev, amount: event.target.value }))
              }
              fullWidth
            />
            {payoutBooking && (
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Payout Breakdown
                </Typography>
                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">Client payment</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(payoutBooking.clientPayment)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">Service fee</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(payoutBooking.serviceBaseAmount)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">Platform fee</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>-{money(payoutBooking.platformFee)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">MLF commission</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>-{money(payoutBooking.commissionAmount)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">Provider payout</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{money(payoutBooking.providerFee)}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    GST included in service fee: {money(payoutBooking.gstIncluded)}
                  </Typography>
                </Stack>
              </Box>
            )}
            <TextField
              label="Payout Notes"
              multiline
              minRows={3}
              value={payoutForm.notes}
              onChange={(event) =>
                setPayoutForm((prev) => ({ ...prev, notes: event.target.value }))
              }
              fullWidth
            />
            <Alert severity="info">
              Confirming this payout transfers the calculated provider amount.
              Platform and MLF fees remain with the platform for the normal payout flow.
            </Alert>
            <Divider />
            <Typography variant="body2" color="text.secondary">
              A unique payment reference number will be generated automatically when payout is confirmed.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmPayout}
            variant="contained"
            sx={pillControlSx}
            disabled={
              Boolean(actionLoading[`payout-${payoutBooking?.booking?.id}`]) ||
              (payoutBooking ? isPayoutConfirmed(payoutBooking) : false) ||
              (payoutBooking ? !hasStripeReference(payoutBooking) : false) ||
              (payoutBooking ? hasRefundActivity(payoutBooking) : false)
            }
          >
            {payoutBooking && isPayoutConfirmed(payoutBooking)
              ? "Payout Already Confirmed"
              : payoutBooking && hasRefundActivity(payoutBooking)
              ? "Refund Active"
              : payoutBooking && !hasStripeReference(payoutBooking)
              ? "No Payment Ref"
              : actionLoading[`payout-${payoutBooking?.booking?.id}`]
              ? "Saving..."
              : "Confirm & Transfer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar({ open: false, message: "", severity: "success" })
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentLifecycle;
